/**
 * PupRing Custom JavaScript - Bundle Builder UI Module
 * =====================================================
 * Buy 3 for $99 Bundle Builder - UI Management
 *
 * Features:
 * - BundleUIManager class with singleton pattern
 * - Product card click handling (debounced)
 * - Quick-view modal management
 * - Progress bar updates (desktop + mobile)
 * - State subscription and reactive UI
 * - Toast notifications
 *
 * Dependencies: custom-bundle-state.js (must load first)
 */

/**
 * BundleUIManager - Handles all UI interactions for bundle builder
 *
 * Responsibilities:
 * - Product card click handling (debounced)
 * - Quick-view modal management
 * - Progress bar updates
 * - State subscription and reactive UI
 */
class BundleUIManager {
  // ═══════════════════════════════════════════════════════════════
  // CONFIGURATION
  // ═══════════════════════════════════════════════════════════════

  static DEBOUNCE_MS = 150;
  static QUICK_VIEW_MODAL_ID = 'bundle-quick-view-modal';

  // Remove tooltip configuration (one-time educational tooltip)
  static REMOVE_TOOLTIP_STORAGE_KEY = 'bundle_remove_tooltip_shown';
  static REMOVE_TOOLTIP_DURATION = 3000; // 3 seconds

  // ═══════════════════════════════════════════════════════════════
  // BUG-034 FIX: SWATCH HELPER METHODS
  // ═══════════════════════════════════════════════════════════════

  /**
   * Get swatch configuration from embedded JSON
   * @returns {Object} Swatch configuration
   */
  static getSwatchConfig() {
    if (!BundleUIManager._swatchConfig) {
      try {
        const configEl = document.getElementById('bundle-swatch-config');
        BundleUIManager._swatchConfig = configEl ? JSON.parse(configEl.textContent) : {
          useVariantImages: true,
          colorSwatchNames: ['color', 'colour', 'material', 'jewelry material'],
          swatchShow: true,
          swatchBaseUrl: ''
        };
      } catch (e) {
        BundleUIManager._swatchConfig = {
          useVariantImages: true,
          colorSwatchNames: ['color', 'colour', 'material', 'jewelry material'],
          swatchShow: true,
          swatchBaseUrl: ''
        };
      }
    }
    return BundleUIManager._swatchConfig;
  }

  /**
   * Check if an option should be rendered as a color swatch
   * @param {string} optionName - The option name (e.g., "Color", "Material")
   * @returns {boolean}
   */
  static isColorOption(optionName) {
    const config = BundleUIManager.getSwatchConfig();
    if (!config.swatchShow) return false;
    return config.colorSwatchNames.includes(optionName.toLowerCase().trim());
  }

  /**
   * Convert option value to swatch filename (replicates Liquid's handleize)
   * @param {string} value - Option value like "Rose Gold"
   * @returns {string} Filename like "swatch-rose-gold.png"
   */
  static getSwatchFilename(value) {
    const handle = value.toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    return `swatch-${handle}.png`;
  }

  /**
   * Get swatch image URL for an option value
   * BUG-032 FIX: Use URL template from Liquid - works for ANY variant value
   * @param {Object} product - Product data
   * @param {number} optionIndex - Zero-based option index
   * @param {string} value - Option value
   * @returns {string} Image URL
   */
  static getSwatchImageUrl(product, optionIndex, value) {
    const config = BundleUIManager.getSwatchConfig();

    // Convert value to handle (e.g., "18k Rose Gold Plated" → "18k-rose-gold-plated")
    const handle = value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

    // Generate URL from template (works for ANY variant value)
    if (config.swatchUrlTemplate) {
      return config.swatchUrlTemplate.replace('{HANDLE}', handle);
    }

    // Fallback to variant image only if template not available
    const optionKey = `option${optionIndex + 1}`;
    const variant = product.variants.find(v => v[optionKey] === value && v.available)
                   || product.variants.find(v => v[optionKey] === value);

    if (variant?.featured_image?.src) {
      return variant.featured_image.src.replace(/\.([^.]+)$/, '_128x128_crop_center.$1');
    }
    return product.featured_image?.replace(/\.([^.]+)$/, '_128x128_crop_center.$1') || '';
  }

  // ═══════════════════════════════════════════════════════════════
  // CONSTRUCTOR
  // ═══════════════════════════════════════════════════════════════

  constructor() {
    this.stateManager = null;
    this.unsubscribe = null;

    // Debounce/lock state
    this.lastClickTime = 0;
    this.isProcessing = false;       // Quick view add in progress
    this.pendingQuickAdd = false;    // Quick-add stock check in progress

    // Cached DOM elements
    this.elements = {
      productCards: null,
      sidebar: null,
      thumbnailContainer: null,
      counter: null,
      nextButton: null,
      mobileBar: null,
      quickViewModal: null
    };

    // Splide instance for quick-view
    this.quickViewSplide = null;
    this.quickViewThumbsSplide = null;

    // Current quick-view product data
    this.currentQuickViewProduct = null;

    // Bound event handlers for proper cleanup (memory leak prevention)
    this.boundInvalidatedHandler = null;
    this.boundThumbnailClickHandler = null;
    this.boundMobileBarClickHandler = null;
    this.boundNextButtonHandler = null;
    this.boundModalClickHandler = null;

    // BUG-060 FIX: OOS modal queue to prevent stacking
    this.oosQueue = [];              // Queue of pending OOS notifications
    this.activeOOSModal = null;      // Currently showing Bootstrap Modal instance
    this.isOOSModalShowing = false;  // Guard flag

    // SESSION-1 FIX: Loading state for Next button
    this.isNextButtonLoading = false;  // Prevents double-clicks and shows spinner

    // SESSION-1 FIX: Toast debouncing to prevent message stacking (BUG-094)
    this.lastToastMessage = null;      // Last toast message shown
    this.lastToastTime = 0;            // Timestamp of last toast
    this.toastDebounceMs = 2000;       // Minimum time between duplicate toasts

    // Remove tooltip state (one-time educational tooltip)
    this.removeTooltipTimeout = null;        // Auto-dismiss timer
    this.isRemoveTooltipShowing = false;     // Guard against multiple tooltips
    this._removeTooltipShownFallback = false; // Fallback if sessionStorage blocked
  }

  // ═══════════════════════════════════════════════════════════════
  // SINGLETON
  // ═══════════════════════════════════════════════════════════════

  static getInstance() {
    if (!window._bundleUIManager) {
      window._bundleUIManager = new BundleUIManager();
    }
    return window._bundleUIManager;
  }

  // ═══════════════════════════════════════════════════════════════
  // INITIALIZATION
  // ═══════════════════════════════════════════════════════════════

  /**
   * Initialize the UI manager
   * Must be called after BundleStateManager is initialized
   */
  async init() {
    // Only init on bundle collection pages
    if (!document.querySelector('[data-bundle-builder]')) {
      return;
    }

    // Get state manager (already initialized)
    this.stateManager = BundleStateManager.getInstance();

    // Wait for state hydration
    await this.stateManager.init();

    // BUG-061 FIX: Cache elements FIRST (needed for loading state)
    this.cacheElements();

    // BUG-061 FIX: Check if products need validation
    const state = this.stateManager.get();
    const hasProductsToValidate = state.products && state.products.length > 0;

    if (hasProductsToValidate) {
      // Show loading skeleton in thumbnail containers
      this.showValidationLoading();

      // AWAIT validation - block until complete (prevents flash of stale state)
      await this.stateManager.validateAndRestoreState().catch(() => {});

      // Remove loading state
      this.hideValidationLoading();
    }

    // Setup click handlers (after validation)
    this.initProductCards();
    this.initProgressBar();
    if (typeof this.initQuickViewModal === 'function') {
      this.initQuickViewModal();
    }

    // Subscribe to state changes
    this.unsubscribe = this.stateManager.subscribe((state) => {
      this.onStateChange(state);
    });

    // Initial UI sync - now with VALIDATED state
    this.onStateChange(this.stateManager.get());

    // Listen for invalidation events (bound handler for cleanup)
    this.boundInvalidatedHandler = (e) => {
      if (e.detail.removed > 0) {
        this.showToast(
          `${e.detail.removed} product(s) removed from your bundle (no longer available)`,
          'warning'
        );
      }
    };
    window.addEventListener('bundle:products-invalidated', this.boundInvalidatedHandler);

    // Phase 9: Listen for OOS variant events
    this.boundOOSHandler = (e) => this.handleVariantOOS(e.detail);
    window.addEventListener('bundle:variant-oos', this.boundOOSHandler);

    // BUG-062 FIX: Handle state corruption notification
    // Check if corruption happened before we initialized (timing gap)
    if (this.stateManager.wasCorrupted) {
      // Defer to ensure DOM is ready
      requestAnimationFrame(() => {
        this.handleStateCorruption({
          reason: this.stateManager.corruptionReason || 'data_corrupted'
        });
      });
    }

    // Also listen for future corruption events (defensive)
    this.boundCorruptionHandler = (e) => this.handleStateCorruption(e.detail);
    window.addEventListener('bundle:state-corrupted', this.boundCorruptionHandler);

    // Phase 2: Listen for customization updates to refresh validation
    this.boundCustomizationHandler = () => {
      const state = this.stateManager.get();
      this.updateMobileBarValidation(state.currentStep);
    };
    document.addEventListener('bundle:customization-updated', this.boundCustomizationHandler);

    // BUG-077 FIX: Removed JS class toggle - body class now added by inline script in Liquid
    // This eliminates layout shift on mobile by reserving space BEFORE any JS runs
  }

  /**
   * Cache DOM element references for performance
   */
  cacheElements() {
    this.elements.productCards = document.querySelectorAll('[data-bundle-product-card]');
    this.elements.sidebar = document.getElementById('bundle-builder-sidebar');
    this.elements.thumbnailContainer = document.querySelector('#bundle-builder-sidebar [data-bundle-thumbnails]');
    this.elements.counter = document.querySelector('#bundle-builder-sidebar [data-bundle-count]');
    this.elements.nextButton = document.querySelector('#bundle-builder-sidebar [data-bundle-next]');
    this.elements.mobileBar = document.getElementById('bundle-mobile-bar');
    this.elements.quickViewModal = document.getElementById(BundleUIManager.QUICK_VIEW_MODAL_ID);
  }

  /**
   * BUG-061 FIX: Show loading skeleton in thumbnail containers during validation
   */
  showValidationLoading() {
    const renderLoading = (container) => {
      if (!container) return;

      container.innerHTML = `
        <div class="bundle-validation-loading">
          <div class="bundle-skeleton bundle-skeleton--thumb"></div>
          <div class="bundle-skeleton bundle-skeleton--thumb"></div>
          <div class="bundle-skeleton bundle-skeleton--thumb"></div>
        </div>
      `;
    };

    renderLoading(this.elements.thumbnailContainer);

    if (this.elements.mobileBar) {
      const mobileThumbs = this.elements.mobileBar.querySelector('[data-bundle-thumbnails]');
      renderLoading(mobileThumbs);
      // BUG-077 FIX: Add validating class for subtle opacity loading state
      this.elements.mobileBar.classList.add('bundle-validating');
    }

    // Show loading state for counter
    if (this.elements.counter) {
      this.elements.counter.textContent = '...';
    }
    if (this.elements.mobileBar) {
      const mobileCounter = this.elements.mobileBar.querySelector('[data-bundle-count]');
      if (mobileCounter) mobileCounter.textContent = '...';
    }
  }

  /**
   * BUG-061 FIX: Hide loading skeleton after validation completes
   */
  hideValidationLoading() {
    // Clear containers - real state rendered by onStateChange()
    const clearContainer = (container) => {
      if (!container) return;
      const loading = container.querySelector('.bundle-validation-loading');
      if (loading) loading.remove();
    };

    clearContainer(this.elements.thumbnailContainer);

    if (this.elements.mobileBar) {
      const mobileThumbs = this.elements.mobileBar.querySelector('[data-bundle-thumbnails]');
      clearContainer(mobileThumbs);
      // BUG-077 FIX: Remove validating class
      this.elements.mobileBar.classList.remove('bundle-validating');
    }
  }

  /**
   * Handle state changes - update all UI elements
   */
  onStateChange(state) {
    this.syncSelectionStates(state);
    this.renderThumbnails(state.products);
    this.updateCounter(state.products.length);
    this.updateNextButton(state.products.length);
    this.updateMobileBarNextText(state.currentStep); // BUG-084 FIX
    this.updateMobileBarLayout(state.currentStep); // Design refactor: step-aware layout
  }

  // ═══════════════════════════════════════════════════════════════
  // PRODUCT CARD HANDLERS
  // ═══════════════════════════════════════════════════════════════

  /**
   * Initialize click handlers on product cards
   */
  initProductCards() {
    // Use event delegation on the product list container
    const productList = document.querySelector('.product-list');
    if (!productList) return;

    productList.addEventListener('click', (e) => {
      // Check for "Add to Bundle" button click
      const addButton = e.target.closest('[data-bundle-add-button]');
      if (addButton) {
        e.preventDefault();
        e.stopPropagation(); // Don't trigger image click
        this.handleQuickAdd(addButton);
        return;
      }

      // Check for product image click
      const imageWrapper = e.target.closest('[data-bundle-image-click]');
      if (imageWrapper) {
        e.preventDefault();
        const card = imageWrapper.closest('[data-bundle-product-card]');
        if (card) {
          this.handleImageClick(card);
        }
      }
    });
  }

  /**
   * Handle quick-add button click
   * Adds product with the card's variant, or an in-stock variant with the same swatch value (no modal)
   * BUG-093 FIX: Also handles remove when bundle is full and product is in bundle
   */
  async handleQuickAdd(button) {
    // Debounce check
    if (Date.now() - this.lastClickTime < BundleUIManager.DEBOUNCE_MS) return;
    this.lastClickTime = Date.now();

    // Lock check: a quick view add or another quick-add stock check is running
    if (this.isProcessing || this.pendingQuickAdd) return;

    const card = button.closest('[data-bundle-product-card]');
    if (!card) return;

    const state = this.stateManager.get();
    const productId = parseInt(card.dataset.productId, 10);

    // BUG-093 FIX: Check if this should be a remove action
    const isInBundle = state.products.some(p => p.productId === productId);
    const bundleFull = state.products.length >= 3;

    if (isInBundle && bundleFull) {
      // The same product can fill several positions: the card button removes the most
      // recently added one (the thumbnails remove a specific position)
      const index = state.products.map(p => p.productId).lastIndexOf(productId);
      const removed = this.stateManager.removeProductByIndex(index);
      if (removed) {
        this.showToast('Product removed from bundle', 'info');

        // Show one-time educational tooltip pointing at thumbnail X buttons
        // Small delay ensures toast renders first for clear visual sequence
        setTimeout(() => {
          this.showRemoveTooltip();
        }, 100);
      }
      return;
    }

    // Check if bundle is full (product NOT in bundle)
    if (bundleFull) {
      this.showToast("You've selected 3 products. Remove one to add another.", 'warning');
      return;
    }

    // Get product data from card
    const productHandle = card.dataset.productHandle;
    const productTitle = card.dataset.productTitle;
    const variantId = parseInt(card.dataset.variantId, 10);
    const variantTitle = card.dataset.variantTitle;
    const featuredImage = card.dataset.featuredImage;
    const variantImage = card.dataset.variantImage;

    // The card's first available variant was in stock when the page rendered; a swatch can
    // point the card at another variant, which is checked against current stock
    let stock = null;
    if (card.dataset.availableVariantId !== String(variantId)) {
      this.pendingQuickAdd = true;
      const restoreButton = this.setQuickAddBusy(button);
      try {
        stock = await this.getQuickAddVariant(productHandle, variantId);
      } finally {
        restoreButton();
        this.pendingQuickAdd = false;
      }
    }

    if (stock && !stock.variant) {
      if (stock.productSoldOut) {
        this.showToast('This product is sold out.', 'warning');
        button.disabled = true;
        button.textContent = card.dataset.textSoldOut || button.textContent;
      } else {
        this.showToast('This variant is sold out.', 'warning');
      }
      return;
    }

    const replaced = stock && stock.variant.id !== variantId;
    if (replaced && stock.cardVariant) {
      this.showToast(`${stock.cardVariant.title} is sold out, so ${stock.variant.title} was added.`, 'info');
    }

    // Add to state
    const success = this.stateManager.addProduct({
      productId,
      productHandle,
      productTitle,
      featuredImage,
      selectedVariantId: replaced ? stock.variant.id : variantId,
      selectedVariantTitle: replaced ? stock.variant.title : variantTitle,
      selectedVariantImage: (replaced ? stock.variant.featured_image?.src : variantImage) || featuredImage
    });

    if (success) {
      // Flash button feedback
      this.flashButtonFeedback(button);
    } else if (this.stateManager.get().products.length >= 3) {
      // The quick view filled the bundle while stock was being checked
      this.showToast("You've selected 3 products. Remove one to add another.", 'warning');
    }
  }

  /**
   * Show a busy state on a quick-add button while its stock is checked
   * @param {HTMLElement} button - Quick-add button
   * @returns {Function} Restores the button
   */
  setQuickAddBusy(button) {
    const label = button.textContent;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.innerHTML = `
      <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
      ${this.escapeHtml(label.trim())}
    `;

    return () => {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      button.textContent = label;
    };
  }

  /**
   * Check the card's variant against current stock. When it is sold out, use the in-stock
   * variant that keeps its swatch (color/material) value and the most other options.
   * Resolves null when stock cannot be checked (the card's variant is kept; state validation
   * re-checks it on reload).
   * @param {string} productHandle - Product handle
   * @param {number} variantId - Variant ID from the card
   * @returns {Promise<Object|null>} { variant, cardVariant, productSoldOut } or null;
   *   variant is null when nothing with the card's swatch value is in stock
   */
  async getQuickAddVariant(productHandle, variantId) {
    let product;
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await BundleStateManager.fetchWithTimeout(
        `${root}products/${encodeURIComponent(productHandle)}.js`,
        {},
        5000
      );
      if (!response.ok) return null;
      product = await response.json();
    } catch (e) {
      return null;
    }

    const variants = Array.isArray(product?.variants) ? product.variants : [];
    if (variants.length === 0) return null;

    const cardVariant = variants.find(v => v.id === variantId) || null;
    const productSoldOut = !variants.some(v => v.available);
    if (cardVariant?.available) return { variant: cardVariant, cardVariant, productSoldOut };

    const optionNames = (product.options || []).map(o => (typeof o === 'string' ? o : o.name));
    let variant = null;
    let bestKept = -1;
    variants.forEach(candidate => {
      if (!candidate.available) return;
      let kept = 0;
      for (let i = 0; cardVariant && i < optionNames.length; i++) {
        const key = `option${i + 1}`;
        const keeps = candidate[key] === cardVariant[key];
        if (!keeps && BundleUIManager.isColorOption(optionNames[i])) return;
        if (keeps) kept++;
      }
      if (kept > bestKept) {
        variant = candidate;
        bestKept = kept;
      }
    });

    return { variant, cardVariant, productSoldOut };
  }

  /**
   * Handle product image click - open quick-view modal
   */
  async handleImageClick(card) {
    const productHandle = card.dataset.productHandle;
    if (!productHandle) return;

    await this.openQuickView(productHandle);
  }

  /**
   * Flash "Added!" feedback on button
   */
  flashButtonFeedback(button) {
    const originalText = button.textContent;
    button.textContent = 'Added!';
    button.classList.add('btn-success');
    button.classList.remove('btn-primary');
    button.disabled = true;

    setTimeout(() => {
      button.textContent = originalText;
      button.classList.remove('btn-success');
      button.classList.add('btn-primary');
      button.disabled = false;
    }, 1000);
  }


  // ═══════════════════════════════════════════════════════════════
  // QUICK-VIEW MODAL (SPLIT TO SEPARATE FILE)
  // Methods moved to: custom-bundle-ui-quickview.js
  // Loaded via prototype extension after this file
  // ═══════════════════════════════════════════════════════════════

  // ═══════════════════════════════════════════════════════════════
  // PROGRESS BAR
  // ═══════════════════════════════════════════════════════════════

  /**
   * Initialize progress bar handlers
   * Uses bound handlers for proper cleanup (memory leak prevention)
   */
  initProgressBar() {
    // Desktop sidebar thumbnail clicks (bound handler)
    this.boundThumbnailClickHandler = (e) => {
      const thumbnail = e.target.closest('[data-bundle-thumbnail-index]');
      if (thumbnail) {
        const index = parseInt(thumbnail.dataset.bundleThumbnailIndex, 10);
        this.removeProductByIndex(index);
      }
    };
    if (this.elements.thumbnailContainer) {
      this.elements.thumbnailContainer.addEventListener('click', this.boundThumbnailClickHandler);
    }

    // Mobile bar clicks (bound handler) - handles thumbnails and back button
    this.boundMobileBarClickHandler = (e) => {
      // Handle thumbnail removal clicks
      const thumbnail = e.target.closest('[data-bundle-thumbnail-index]');
      if (thumbnail) {
        const index = parseInt(thumbnail.dataset.bundleThumbnailIndex, 10);
        this.removeProductByIndex(index);
        return;
      }

      // Design refactor: Handle mobile Back button clicks
      // BUG-118 FIX: Also handle Together mode
      const backBtn = e.target.closest('[data-bundle-mobile-back]');
      if (backBtn) {
        e.preventDefault();
        const state = this.stateManager.get();

        // Delegate to the appropriate in-page back button
        if (state.currentStep === 'together') {
          const togetherBackBtn = document.querySelector('[data-bundle-together-back]');
          if (togetherBackBtn) togetherBackBtn.click();
        } else {
          const inPageBackBtn = document.querySelector('[data-bundle-individual-back]');
          if (inPageBackBtn) inPageBackBtn.click();
        }
        return;
      }

      // BUG-123 FIX: Handle mobile switch mode link (Together → Individual)
      // The link exists in mobile bar but had no click handler - only desktop version worked
      const switchMode = e.target.closest('[data-bundle-switch-mode]');
      if (switchMode) {
        e.preventDefault();
        const state = this.stateManager.get();
        if (state.currentStep === 'together') {
          // Same logic as BundleTogetherManager.handleSwitchMode()
          this.stateManager.set({
            customizationMode: 'individual',
            unifiedCustomization: null
          });
          this.showToast('Switched to individual customization', 'info');
          this.stateManager.goToStep('individual-1');
        }
      }
    };
    if (this.elements.mobileBar) {
      this.elements.mobileBar.addEventListener('click', this.boundMobileBarClickHandler);
    }

    // Next button handler (bound handler)
    this.boundNextButtonHandler = (e) => {
      if (e.target.closest('[data-bundle-next]')) {
        e.preventDefault();
        this.handleNextClick();
      }
    };
    document.addEventListener('click', this.boundNextButtonHandler);
  }

  /**
   * Sync product card selection states
   * BUG-093 FIX: Also updates button text/style based on bundle state
   */
  syncSelectionStates(state) {
    const bundleFull = state.products.length >= 3;

    // Reset all cards AND buttons
    document.querySelectorAll('[data-bundle-product-card]').forEach(card => {
      card.classList.remove('bundle-product--selected');
      const indicator = card.querySelector('.bundle-selection-indicator');
      if (indicator) indicator.remove();

      // BUG-093 FIX: Reset button to default "Add to bundle" state
      const button = card.querySelector('[data-bundle-add-button]');
      if (button && !button.disabled) {
        const addText = button.dataset.textAddToBundle || 'Add to bundle';
        button.textContent = addText;
        button.classList.remove('btn-danger');
        button.classList.add('btn-primary');
      }
    });

    // Track ALL position indices per product ID
    const productIndices = {};
    state.products.forEach((p, index) => {
      if (!productIndices[p.productId]) {
        productIndices[p.productId] = [];
      }
      productIndices[p.productId].push(index + 1); // 1-based positions
    });

    // Apply selection states
    Object.keys(productIndices).forEach(productId => {
      const card = document.querySelector(`[data-bundle-product-card][data-product-id="${productId}"]`);
      if (!card) return;

      const indices = productIndices[productId];
      card.classList.add('bundle-product--selected');

      // Add selection indicator
      const imgWrapper = card.querySelector('.img-wrapper');
      if (imgWrapper && !imgWrapper.querySelector('.bundle-selection-indicator')) {
        const indicator = document.createElement('div');
        indicator.className = 'bundle-selection-indicator';

        indicator.innerHTML = `
          <span class="bundle-order">${indices.join('-')}</span>
        `;
        imgWrapper.appendChild(indicator);
      }

      // BUG-093 FIX: If bundle is full, show REMOVE button for selected products
      if (bundleFull) {
        const button = card.querySelector('[data-bundle-add-button]');
        if (button && !button.disabled) {
          button.textContent = 'REMOVE';
          button.classList.remove('btn-primary');
          button.classList.add('btn-danger');
        }
      }
    });
  }

  /**
   * Render thumbnails in progress bar
   */
  renderThumbnails(products) {
    const renderToContainer = (container) => {
      if (!container) return;

      const slots = [];
      for (let i = 0; i < 3; i++) {
        const product = products[i];
        if (product) {
          const image = product.selectedVariantImage || product.featuredImage;
          // BUG-097 FIX: Show product name in tooltip
          const title = this.escapeHtml(product.productTitle);
          slots.push(`
            <div
              class="bundle-thumbnail bundle-thumbnail--filled"
              data-bundle-thumbnail-index="${i}"
              title="${title}"
            >
              <img src="${this.escapeHtml(this.resizeImage(image, '80x80'))}" alt="${title}" width="50" height="50" loading="eager">
              <span class="bundle-thumbnail-remove">×</span>
            </div>
          `);
        } else {
          slots.push(`
            <div class="bundle-thumbnail bundle-thumbnail--empty">
              <span>+</span>
            </div>
          `);
        }
      }

      container.innerHTML = slots.join('');
    };

    // Desktop sidebar
    renderToContainer(this.elements.thumbnailContainer);

    // Mobile bar
    if (this.elements.mobileBar) {
      const mobileThumbs = this.elements.mobileBar.querySelector('[data-bundle-thumbnails]');
      renderToContainer(mobileThumbs);
    }
  }

  /**
   * Update counter display
   */
  updateCounter(count) {
    const updateEl = (el) => {
      if (el) el.textContent = count;
    };

    updateEl(this.elements.counter);

    if (this.elements.mobileBar) {
      updateEl(this.elements.mobileBar.querySelector('[data-bundle-count]'));
    }
  }

  /**
   * Update next button state
   * BUG-098 FIX: Adds attention animation to mobile button when first enabled
   */
  updateNextButton(count) {
    const updateBtn = (btn, isMobile = false) => {
      if (!btn) return;

      const wasDisabled = btn.disabled;

      if (count >= 3) {
        btn.disabled = false;
        btn.classList.remove('btn-secondary');
        btn.classList.add('btn-primary');

        // BUG-098: Mobile attention animation when first enabled
        if (isMobile && wasDisabled) {
          btn.classList.remove('bundle-next-enabled');
          btn.classList.add('bundle-next-just-enabled');

          // After initial animation, switch to persistent subtle pulse
          setTimeout(() => {
            btn.classList.remove('bundle-next-just-enabled');
            btn.classList.add('bundle-next-enabled');
          }, 1200);
        } else if (isMobile) {
          btn.classList.add('bundle-next-enabled');
        }
      } else {
        btn.disabled = true;
        btn.classList.remove('btn-primary', 'bundle-next-enabled', 'bundle-next-just-enabled');
        btn.classList.add('btn-secondary');
      }
    };

    updateBtn(this.elements.nextButton, false);

    if (this.elements.mobileBar) {
      updateBtn(this.elements.mobileBar.querySelector('[data-bundle-next]'), true);
    }
  }

  /**
   * BUG-084 FIX: Update mobile bar Next button text based on current step
   * Provides contextual labels so users know what action the button performs
   */
  updateMobileBarNextText(step) {
    if (!this.elements.mobileBar) return;

    const btn = this.elements.mobileBar.querySelector('[data-bundle-next]');
    if (!btn) return;

    const labels = {
      'selection': 'Next →',
      'together': 'Continue →',           // BUG-118 FIX: Added for Together mode
      'individual-1': 'Next: Product 2 →',
      'individual-2': 'Next: Product 3 →',
      'individual-3': 'Continue →'
    };

    // Only update for steps where mobile bar button works, otherwise show default
    btn.textContent = labels[step] || 'Next →';
  }

  /**
   * Phase 2: Update mobile bar layout based on current step
   * Selection step shows thumbnails + counter
   * Customization steps (Together + Individual) show Back/Next buttons only (no thumbnails/counter)
   * BUG-118 FIX: Added Together mode support
   */
  updateMobileBarLayout(step) {
    if (!this.elements.mobileBar) return;

    const thumbsContainer = this.elements.mobileBar.querySelector('[data-bundle-thumbnails]');
    const counterText = this.elements.mobileBar.querySelector('.bundle-mobile-info'); // FIX: was .bundle-progress-info p
    const isIndividual = step && step.startsWith('individual-');
    const isTogether = step === 'together';                    // BUG-118 FIX
    const isCustomization = isIndividual || isTogether;        // BUG-118 FIX
    const isSelection = step === 'selection';

    // Hide thumbnails on customization steps (both individual AND together)
    if (thumbsContainer) {
      thumbsContainer.style.display = isCustomization ? 'none' : '';
    }

    // Hide "3/3 selected" counter - ONLY show on selection step
    if (counterText) {
      counterText.style.display = isSelection ? '' : 'none';
    }

    // BUG-122 FIX: Show "Customize Individually" link only on Together step
    const switchModeEl = this.elements.mobileBar.querySelector('[data-bundle-switch-mode-mobile]');
    if (switchModeEl) {
      switchModeEl.hidden = !isTogether;
    }

    // Add/remove Back button for customization steps
    this.updateMobileBackButton(step);

    // Update validation text and button state
    this.updateMobileBarValidation(step);
  }

  /**
   * BUG-112: Toggle Back button visibility (pre-rendered in HTML)
   * Uses CSS transitions for smooth animation instead of DOM insertion/removal
   * BUG-118 FIX: Added Together mode support
   */
  updateMobileBackButton(step) {
    if (!this.elements.mobileBar) return;

    const backBtn = this.elements.mobileBar.querySelector('[data-bundle-mobile-back]');
    const nextBtn = this.elements.mobileBar.querySelector('[data-bundle-next]');
    if (!backBtn || !nextBtn) return;

    const isIndividual = step && step.startsWith('individual-');
    const isTogether = step === 'together';                    // BUG-118 FIX
    const showBack = isIndividual || isTogether;               // BUG-118 FIX

    if (showBack) {
      // Show Back button - instant layout, no animation
      backBtn.hidden = false;
      backBtn.classList.add('bundle-mobile-back-visible');
      nextBtn.classList.add('flex-fill');
    } else {
      // Hide Back button with animation
      backBtn.classList.remove('bundle-mobile-back-visible');
      nextBtn.classList.remove('flex-fill');
      // Delay hidden attribute to allow CSS transition to complete
      setTimeout(() => {
        const currentStep = this.stateManager?.get?.()?.currentStep;
        // BUG-118 FIX: Also check for Together mode
        const shouldHide = !currentStep?.startsWith('individual-') && currentStep !== 'together';
        if (shouldHide) {
          backBtn.hidden = true;
        }
      }, 300);
    }
  }

  /**
   * Phase 2: Update mobile bar validation text and button state
   * BUG-113: Now shows "Product X of 3" counter in validation area
   */
  updateMobileBarValidation(step) {
    if (!this.elements.mobileBar) return;

    const validationEl = this.elements.mobileBar.querySelector('[data-bundle-validation]');
    const nextBtn = this.elements.mobileBar.querySelector('[data-bundle-next]');

    if (!step || !step.startsWith('individual-')) {
      // Not on individual step - clear validation
      if (validationEl) validationEl.textContent = '';
      return;
    }

    // Get current product index (individual-1 → 0, individual-2 → 1, etc.)
    const index = parseInt(step.split('-')[1], 10) - 1;
    const state = this.stateManager.get();
    const product = state.products[index];

    if (!product) return;

    // Check what's missing
    const missing = [];

    // Check variant selection
    if (!product.selectedVariantId) {
      missing.push('Choose your material');
    }

    // Check pet name (if required)
    if (product.requirements?.requiresPetName) {
      // Names beyond the pendant count are kept for switching back but do not count
      const petNames = (product.customization?.petNames || []).slice(0, product.pendantCount || 1);
      const hasName = petNames.some(n => n && n.trim().length > 0);
      if (!hasName) {
        missing.push('Add your pet\'s name');
      }
    }

    // Check photo (if required)
    if (product.requirements?.requiresPhoto) {
      if (!product.customization?.photoUrl) {
        missing.push('Upload a photo');
      }
    }

    // BUG-113: Show "Product X of 3" counter in validation area
    // Format: "Product X of 3" or "Product X of 3 • [what's missing]"
    if (validationEl) {
      const counterText = `Product ${index + 1} of 3`;
      if (missing.length > 0) {
        validationEl.textContent = `${counterText} • ${missing[0]}`;
      } else {
        validationEl.textContent = counterText;
      }
    }

    // Update button disabled state
    if (nextBtn) {
      nextBtn.disabled = missing.length > 0;
    }
  }

  /**
   * Remove product by index
   * Removes that exact position (the same product can fill several); the state manager
   * handles step reset and dispatches events
   */
  removeProductByIndex(index) {
    if (this.stateManager.removeProductByIndex(index)) {
      this.showToast('Product removed from bundle', 'info');
    }
  }

  /**
   * Handle Next button click
   * BUG-025 FIX: Selection step uses direct navigation
   * BUG-084 FIX: Individual steps delegate to in-page Continue button
   */
  handleNextClick() {
    const state = this.stateManager.get();
    const currentStep = state.currentStep;

    // BUG-066 FIX: Sync state if UI shows selection but state disagrees
    // This can happen when localStorage has stale step (e.g., 'choice') after page refresh
    // The product grid being visible means user is actually on selection step
    const productGrid = document.querySelector('.product-list');
    const gridVisible = productGrid && productGrid.style.display !== 'none';

    if (currentStep !== 'selection' && gridVisible) {
      // User is viewing selection UI but state is out of sync - fix it
      this.stateManager.set({ currentStep: 'selection' });
      // Fall through to handle as selection step
      this.handleSelectionNext(state);
      return;
    }

    // BUG-084 FIX: Route to appropriate handler based on current step
    switch (currentStep) {
      case 'selection':
        this.handleSelectionNext(state);
        break;

      case 'individual-1':
      case 'individual-2':
      case 'individual-3':
        // BUG-084 FIX: Trigger the Individual mode Continue button
        // This reuses existing validation logic in BundleIndividualManager
        const continueBtn = document.querySelector('[data-bundle-individual-continue]');
        if (continueBtn && !continueBtn.disabled) {
          continueBtn.click();
        }
        break;

      case 'together':
        // BUG-118 FIX: Trigger the Together mode Continue button
        // This reuses existing validation logic in BundleTogetherManager
        const togetherContinueBtn = document.querySelector('[data-bundle-together-continue]');
        if (togetherContinueBtn && !togetherContinueBtn.disabled) {
          togetherContinueBtn.click();
        }
        break;

      default:
        // All other steps (choice, upsells, summary):
        // Keep original BUG-025 behavior - do nothing
        // These steps have visible in-page buttons that users should use
        return;
    }
  }

  /**
   * Handle Next button click from selection step
   * BUG-084 FIX: Extracted from handleNextClick() for clarity
   * SESSION-1 FIX: Added loading state with spinner during choice step fetch
   */
  handleSelectionNext(state) {
    if (state.products.length < 3) {
      this.showToast('Please select 3 products to continue', 'warning');
      return;
    }

    // SESSION-1 FIX: Prevent double-clicks while loading
    if (this.isNextButtonLoading) {
      return;
    }

    // SESSION-1 FIX: Show loading spinner on Next button
    this.setNextButtonLoading(true);

    // Navigate to choice step (goToStep dispatches bundle:step-changed event)
    // The loading state will be cleared by the choice manager when ready
    const success = this.stateManager.goToStep('choice');

    if (success) {
      // Scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // Navigation failed - clear loading state
      this.setNextButtonLoading(false);
    }
  }

  /**
   * SESSION-1 FIX: Set loading state on Next button(s)
   * Shows spinner and disables button to prevent double-clicks
   * @param {boolean} loading - Whether to show loading state
   */
  setNextButtonLoading(loading) {
    this.isNextButtonLoading = loading;

    const updateBtn = (btn) => {
      if (!btn) return;

      if (loading) {
        // Store original text and show spinner
        if (!btn.dataset.originalText) {
          btn.dataset.originalText = btn.textContent;
        }
        btn.disabled = true;
        btn.innerHTML = `
          <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
          Loading...
        `;
        btn.classList.add('bundle-next-loading');
      } else {
        // Restore original text
        btn.disabled = false;
        btn.textContent = btn.dataset.originalText || 'Next →';
        btn.classList.remove('bundle-next-loading');
        delete btn.dataset.originalText;
      }
    };

    // Update desktop sidebar button
    updateBtn(this.elements.nextButton);

    // Update mobile bar button
    if (this.elements.mobileBar) {
      updateBtn(this.elements.mobileBar.querySelector('[data-bundle-next]'));
    }
  }

  // ═══════════════════════════════════════════════════════════════
  // UTILITIES
  // ═══════════════════════════════════════════════════════════════

  /**
   * Show toast notification
   * SESSION-1 FIX (BUG-094): Added debouncing to prevent duplicate toasts from stacking
   */
  showToast(message, type = 'info') {
    // SESSION-1 FIX (BUG-094): Debounce duplicate messages
    const now = Date.now();
    if (message === this.lastToastMessage && (now - this.lastToastTime) < this.toastDebounceMs) {
      // Skip duplicate message within debounce window
      return;
    }
    this.lastToastMessage = message;
    this.lastToastTime = now;

    // Create toast container if not exists
    let container = document.querySelector('.bundle-toast-container');
    if (!container) {
      container = document.createElement('div');
      container.className = 'bundle-toast-container';
      document.body.appendChild(container);
    }

    // SESSION-1 FIX: Limit max visible toasts to prevent stacking
    const existingToasts = container.querySelectorAll('.bundle-toast');
    if (existingToasts.length >= 3) {
      // Remove oldest toast to make room
      existingToasts[0].remove();
    }

    const toast = document.createElement('div');
    toast.className = `bundle-toast bundle-toast--${type}`;
    toast.textContent = message;

    container.appendChild(toast);

    // Trigger animation
    requestAnimationFrame(() => {
      toast.classList.add('bundle-toast--visible');
    });

    // Remove after delay
    setTimeout(() => {
      toast.classList.remove('bundle-toast--visible');
      setTimeout(() => toast.remove(), 300);
    }, 3000);
  }

  /**
   * Handle state corruption - show user notification
   * BUG-062 FIX: Notify user when bundle data is lost
   * @param {Object} detail - Corruption details
   */
  handleStateCorruption(detail) {
    const messages = {
      'storage_full': 'Your browser storage is full. Bundle progress has been reset.',
      'storage_blocked': 'Your browser blocked storage access. Bundle progress will not be saved.',
      'data_corrupted': 'Your bundle data was corrupted and has been reset. We apologize for the inconvenience.',
      'unknown': 'There was an issue loading your bundle. Starting fresh.'
    };

    const message = messages[detail.reason] || messages['unknown'];
    this.showToast(message, 'warning');

    // Clear the flag so we don't show again on reinit
    if (this.stateManager) {
      this.stateManager.wasCorrupted = false;
      this.stateManager.corruptionReason = null;
    }
  }

  /**
   * Resize Shopify image URL
   */
  resizeImage(url, size) {
    if (!url) return '';

    // Handle Shopify CDN URLs
    if (url.includes('cdn.shopify.com')) {
      return url.replace(/(_\d+x\d+)?(\.[a-z]+)(\?.*)?$/i, `_${size}$2`);
    }

    // Handle file asset URLs
    if (url.includes('?')) {
      return `${url}&width=${size.split('x')[0]}`;
    }

    return url;
  }

  /**
   * Preload all product images for instant variant switching
   * BUG-020 FIX: Images use loading="lazy" which causes lag when Splide transitions
   * By preloading all images when modal opens, they're cached and ready for instant display
   */
  preloadProductImages(product) {
    if (!product || !product.images) return;

    product.images.forEach((imgUrl, i) => {
      if (i === 0) return; // First image already uses loading="eager"

      const img = new Image();
      img.src = this.resizeImage(imgUrl, '600x600');
    });
  }

  /**
   * Get loading skeleton HTML
   */
  getLoadingSkeleton() {
    return `
      <div class="row g-4">
        <div class="col-12 col-md-6">
          <div class="bundle-skeleton bundle-skeleton--image"></div>
        </div>
        <div class="col-12 col-md-6">
          <div class="bundle-skeleton bundle-skeleton--title"></div>
          <div class="bundle-skeleton bundle-skeleton--text mt-3"></div>
          <div class="bundle-skeleton bundle-skeleton--text mt-2" style="width: 60%"></div>
          <div class="bundle-skeleton bundle-skeleton--button mt-4"></div>
        </div>
      </div>
    `;
  }

  // ═══════════════════════════════════════════════════════════════
  // PHASE 9: OOS VARIANT HANDLING
  // ═══════════════════════════════════════════════════════════════

  /**
   * Handle variant OOS event - queue notification for display
   * BUG-060 FIX: Queue OOS notifications instead of showing immediately
   * Only one modal shows at a time; subsequent OOS variants are queued
   *
   * @param {Object} detail - Event detail { productIndex, productTitle, variantTitle, productId }
   */
  handleVariantOOS(detail) {
    // BUG-060 FIX: Add to queue instead of showing immediately
    this.oosQueue.push(detail);

    // Process queue if no modal currently showing
    if (!this.isOOSModalShowing) {
      this.processOOSQueue();
    }
  }

  /**
   * Process the OOS notification queue
   * BUG-060 FIX: Shows one modal at a time, processes next on dismiss
   */
  processOOSQueue() {
    // Exit if queue empty or modal already showing
    if (this.oosQueue.length === 0 || this.isOOSModalShowing) {
      return;
    }

    // Get next item from queue
    const detail = this.oosQueue.shift();
    const { productIndex, productTitle, variantTitle, productId } = detail;

    // Set guard flag
    this.isOOSModalShowing = true;

    // Create and show OOS modal
    const modal = this.createOOSModal(productIndex, productTitle, variantTitle);
    document.body.appendChild(modal);

    // Initialize Bootstrap modal
    const bsModal = new bootstrap.Modal(modal);
    this.activeOOSModal = bsModal;

    // Bind action handlers
    modal.querySelector('[data-bundle-oos-remove]')?.addEventListener('click', () => {
      bsModal.hide();
      const products = this.stateManager.get().products;
      const index = products[productIndex]?.productId === productId
        ? productIndex
        : products.findIndex(p => p.productId === productId);
      if (this.stateManager.removeProductByIndex(index)) {
        this.showToast('Product removed from bundle', 'info');
      }
    });

    modal.querySelector('[data-bundle-oos-reselect]')?.addEventListener('click', () => {
      bsModal.hide();
      // Navigate back to selection step
      this.stateManager.goToStep('selection');
      this.showToast('Please select new options for your product', 'info');
    });

    // BUG-060 FIX: On modal hidden, cleanup and process next in queue
    modal.addEventListener('hidden.bs.modal', () => {
      // Cleanup this modal
      modal.remove();
      this.activeOOSModal = null;
      this.isOOSModalShowing = false;

      // Process next item in queue (if any)
      this.processOOSQueue();
    });

    // Show modal
    bsModal.show();
  }

  /**
   * Create OOS modal HTML
   * Phase 9: Modal with options to remove product or re-select variant
   *
   * @param {number} productIndex - Index of product (0, 1, 2)
   * @param {string} productTitle - Product title
   * @param {string} variantTitle - Out of stock variant title
   * @returns {HTMLElement} Modal element
   */
  createOOSModal(productIndex, productTitle, variantTitle) {
    const modal = document.createElement('div');
    modal.id = 'bundle-oos-modal';
    modal.className = 'modal fade';
    modal.setAttribute('tabindex', '-1');
    modal.setAttribute('aria-hidden', 'true');

    modal.innerHTML = `
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header border-0">
            <h5 class="modal-title">Option No Longer Available</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="alert alert-warning mb-3">
              <strong>Product ${productIndex + 1}:</strong> ${this.escapeHtml(productTitle)}
            </div>
            <p class="text-muted mb-3">
              The option "<strong>${this.escapeHtml(variantTitle)}</strong>" you previously selected is now sold out.
            </p>
            <p class="mb-0">What would you like to do?</p>
          </div>
          <div class="modal-footer flex-column border-0 pt-0">
            <button type="button" class="btn btn-primary w-100" data-bundle-oos-reselect>
              Select New Option
            </button>
            <button type="button" class="btn btn-outline-danger w-100" data-bundle-oos-remove>
              Remove Product from Bundle
            </button>
          </div>
        </div>
      </div>
    `;

    return modal;
  }

  /**
   * Escape HTML for safe rendering in text and quoted attributes
   * @param {string} str - String to escape
   * @returns {string} Escaped string
   */
  escapeHtml(str) {
    if (str === null || str === undefined) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // ═══════════════════════════════════════════════════════════════
  // REMOVE TOOLTIP - One-time educational tooltip
  // Shows when user clicks REMOVE button, points at thumbnail X buttons
  // ═══════════════════════════════════════════════════════════════

  /**
   * Check if remove tooltip should be shown (once per session)
   * @returns {boolean}
   */
  shouldShowRemoveTooltip() {
    try {
      return sessionStorage.getItem(BundleUIManager.REMOVE_TOOLTIP_STORAGE_KEY) !== 'true';
    } catch (e) {
      // sessionStorage not available (incognito mode, etc.) - use memory flag
      return !this._removeTooltipShownFallback;
    }
  }

  /**
   * Mark remove tooltip as shown (sessionStorage + fallback)
   */
  markRemoveTooltipShown() {
    try {
      sessionStorage.setItem(BundleUIManager.REMOVE_TOOLTIP_STORAGE_KEY, 'true');
    } catch (e) {
      // Fallback to memory flag if sessionStorage blocked
      this._removeTooltipShownFallback = true;
    }
  }

  /**
   * Show educational tooltip pointing at thumbnail removal area
   * Only shows once per session to avoid annoyance
   */
  showRemoveTooltip() {
    // Guard: already showing or already shown this session
    if (this.isRemoveTooltipShowing || !this.shouldShowRemoveTooltip()) {
      return;
    }

    // Determine mobile vs desktop
    const isMobile = window.innerWidth < 992;

    // Find target thumbnail container
    const targetSelector = isMobile
      ? '#bundle-mobile-bar [data-bundle-thumbnails]'
      : '#bundle-builder-sidebar [data-bundle-thumbnails]';
    const targetEl = document.querySelector(targetSelector);

    // Abort if target not found (shouldn't happen on selection step)
    if (!targetEl) return;

    // Mark as shown immediately (before animation completes)
    this.markRemoveTooltipShown();
    this.isRemoveTooltipShowing = true;

    // Create tooltip element with dynamic copy based on viewport
    const tooltip = document.createElement('div');
    tooltip.className = 'bundle-remove-tooltip';
    tooltip.textContent = isMobile
      ? 'If you want to remove more items, click the × on your selected items below'
      : 'If you want to remove more items, click the × on your selected items in the sidebar';
    tooltip.setAttribute('role', 'tooltip');
    tooltip.setAttribute('aria-live', 'polite');

    document.body.appendChild(tooltip);

    // Position tooltip relative to target
    this.positionRemoveTooltip(tooltip, targetEl, isMobile);

    // Highlight X buttons on thumbnails
    this.highlightThumbnailRemoveButtons();

    // Trigger enter animation on next frame
    requestAnimationFrame(() => {
      tooltip.classList.add('bundle-remove-tooltip--visible');
    });

    // Click anywhere to dismiss early
    const dismissHandler = () => {
      this.hideRemoveTooltip(tooltip, dismissHandler);
    };
    // Use setTimeout to avoid immediate dismissal from the remove button click
    setTimeout(() => {
      document.addEventListener('click', dismissHandler, { once: true });
    }, 50);

    // Auto-dismiss after 3 seconds
    this.removeTooltipTimeout = setTimeout(() => {
      this.hideRemoveTooltip(tooltip, dismissHandler);
    }, BundleUIManager.REMOVE_TOOLTIP_DURATION);

    // Dismiss on window resize (position may become invalid)
    const resizeHandler = () => {
      this.hideRemoveTooltip(tooltip, dismissHandler);
      window.removeEventListener('resize', resizeHandler);
    };
    window.addEventListener('resize', resizeHandler, { once: true });
  }

  /**
   * Position tooltip relative to target element
   * @param {HTMLElement} tooltip - Tooltip element
   * @param {HTMLElement} targetEl - Target thumbnail container
   * @param {boolean} isMobile - Whether in mobile viewport
   */
  positionRemoveTooltip(tooltip, targetEl, isMobile) {
    const targetRect = targetEl.getBoundingClientRect();

    if (isMobile) {
      // Mobile: position ABOVE the mobile bar
      // Arrow points DOWN at thumbnails
      // CSS handles left/right (full width minus padding)
      tooltip.style.bottom = `${window.innerHeight - targetRect.top + 12}px`;
    } else {
      // Desktop: position to the RIGHT of sidebar thumbnail area
      // Arrow points LEFT at thumbnails
      tooltip.style.top = `${targetRect.top + targetRect.height / 2}px`;
      tooltip.style.left = `${targetRect.right + 16}px`;
    }
  }

  /**
   * Highlight X buttons on thumbnails with pulse animation
   */
  highlightThumbnailRemoveButtons() {
    // Find all remove buttons in both desktop and mobile containers
    const removeButtons = document.querySelectorAll('.bundle-thumbnail-remove');
    removeButtons.forEach(btn => {
      btn.classList.add('bundle-thumbnail-remove--highlighted');
      // Remove class after animation completes (2 pulses × 0.5s = 1s)
      setTimeout(() => {
        btn.classList.remove('bundle-thumbnail-remove--highlighted');
      }, 1000);
    });
  }

  /**
   * Hide and cleanup remove tooltip
   * @param {HTMLElement} tooltip - Tooltip element to remove
   * @param {Function} dismissHandler - Click handler to remove (if exists)
   */
  hideRemoveTooltip(tooltip, dismissHandler) {
    // Guard against double-calls
    if (!tooltip || !tooltip.parentNode) return;

    // Clear auto-dismiss timeout
    if (this.removeTooltipTimeout) {
      clearTimeout(this.removeTooltipTimeout);
      this.removeTooltipTimeout = null;
    }

    // Remove click listener (may already be removed by { once: true })
    if (dismissHandler) {
      document.removeEventListener('click', dismissHandler);
    }

    // Animate out
    tooltip.classList.remove('bundle-remove-tooltip--visible');

    // Remove from DOM after transition completes
    setTimeout(() => {
      if (tooltip.parentNode) {
        tooltip.remove();
      }
      this.isRemoveTooltipShowing = false;
    }, 300); // Match CSS transition duration
  }

  // ═══════════════════════════════════════════════════════════════
  // CLEANUP
  // ═══════════════════════════════════════════════════════════════

  /**
   * Cleanup for theme editor
   * Removes all event listeners to prevent memory leaks
   */
  cleanup() {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }

    // Remove bound event handlers (memory leak prevention)
    if (this.boundInvalidatedHandler) {
      window.removeEventListener('bundle:products-invalidated', this.boundInvalidatedHandler);
      this.boundInvalidatedHandler = null;
    }

    // Phase 9: Remove OOS handler
    if (this.boundOOSHandler) {
      window.removeEventListener('bundle:variant-oos', this.boundOOSHandler);
      this.boundOOSHandler = null;
    }

    // BUG-060 FIX: Cleanup OOS modal queue
    this.oosQueue = [];
    this.isOOSModalShowing = false;
    if (this.activeOOSModal) {
      try {
        this.activeOOSModal.hide();
      } catch (e) {
        // Modal may already be disposed
      }
      this.activeOOSModal = null;
    }
    // Remove any orphaned OOS modals from DOM
    document.querySelectorAll('#bundle-oos-modal').forEach(el => el.remove());

    // Remove tooltip cleanup
    if (this.removeTooltipTimeout) {
      clearTimeout(this.removeTooltipTimeout);
      this.removeTooltipTimeout = null;
    }
    this.isRemoveTooltipShowing = false;
    const existingTooltip = document.querySelector('.bundle-remove-tooltip');
    if (existingTooltip) existingTooltip.remove();

    // BUG-062 FIX: Remove corruption handler
    if (this.boundCorruptionHandler) {
      window.removeEventListener('bundle:state-corrupted', this.boundCorruptionHandler);
      this.boundCorruptionHandler = null;
    }

    // Phase 2: Remove customization handler
    if (this.boundCustomizationHandler) {
      document.removeEventListener('bundle:customization-updated', this.boundCustomizationHandler);
      this.boundCustomizationHandler = null;
    }

    // Remove progress bar handlers
    if (this.boundThumbnailClickHandler && this.elements.thumbnailContainer) {
      this.elements.thumbnailContainer.removeEventListener('click', this.boundThumbnailClickHandler);
    }
    this.boundThumbnailClickHandler = null;

    if (this.boundMobileBarClickHandler && this.elements.mobileBar) {
      this.elements.mobileBar.removeEventListener('click', this.boundMobileBarClickHandler);
    }
    this.boundMobileBarClickHandler = null;

    if (this.boundNextButtonHandler) {
      document.removeEventListener('click', this.boundNextButtonHandler);
      this.boundNextButtonHandler = null;
    }

    // Remove modal delegation handler and reset flag
    if (this.boundModalClickHandler && this.elements.quickViewModal) {
      this.elements.quickViewModal.removeEventListener('click', this.boundModalClickHandler);
      this.elements.quickViewModal._bundleVariantDelegationInitialized = false;
    }
    this.boundModalClickHandler = null;

    // Clean up Splide instances
    if (this.quickViewSplide) {
      this.quickViewSplide.destroy();
      this.quickViewSplide = null;
    }

    if (this.quickViewThumbsSplide) {
      this.quickViewThumbsSplide.destroy();
      this.quickViewThumbsSplide = null;
    }

    // Remove toast container
    const toastContainer = document.getElementById('bundle-toast-container');
    if (toastContainer) {
      toastContainer.remove();
    }

    // BUG-077 FIX: Removed - body class handled by inline script in Liquid template
  }

  /**
   * Reinitialize for theme editor
   * BUG-055 FIX: Made async to await init()
   */
  async reinit() {
    this.cleanup();
    this.cacheElements();
    await this.init();
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// INITIALIZATION
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Initialize bundle UI manager when DOM is ready
 * Called after BundleStateManager is initialized
 */
const initBundleUIManager = async () => {
  const manager = BundleUIManager.getInstance();
  await manager.init();

  // Expose globally for console access and other modules
  window.bundleUI = manager;

  // Initialize step router (from custom-bundle-router.js)
  if (typeof BundleStepRouter !== 'undefined') {
    const stateManager = BundleStateManager.getInstance();
    const router = new BundleStepRouter(stateManager, manager);
    // BUG-055 FIX: Await router.init() to ensure page refresh state is properly restored
    await router.init();

    // Expose router globally for console access
    window.bundleRouter = router;

    // Listen for theme editor section events
    // BUG-055 FIX: Made async to await reinit() calls
    document.addEventListener('shopify:section:load', async (e) => {
      if (e.target.querySelector('[data-bundle-builder]')) {
        await manager.reinit();
        await router.reinit();
      }
    });

    document.addEventListener('shopify:section:unload', (e) => {
      if (e.target.querySelector('[data-bundle-builder]')) {
        manager.cleanup();
        router.cleanup();
      }
    });
  }
};

// Export class for other modules
window.BundleUIManager = BundleUIManager;
